import { ACKNOWLEDGE_LUX_PLUS_MONTH_FREE_PREVIEW_MODAL } from 'actions/actionConstants'
import FormatCurrency from 'components/Common/FormatCurrency'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getImageUrl } from 'lib/image/imageUtils'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineUnlockAltIcon from 'LuxKit/Icons/line/LineUnlockAltIcon'
import SolidTagAltIcon from 'LuxKit/Icons/solid/SolidTagAltIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter, { ModalFooterOnClickHandler } from 'LuxKit/Modal/ModalFooter'
import Pill from 'LuxKit/Pill'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

const Hero = styled(Sheet)`
  background:
    url(${getImageUrl('lux-plus-month-plus-texture-desktop')}) no-repeat bottom center / 100%,
    radial-gradient(100% 101.45% at 0% 1.74%, #4866DE 0%, #4054CB 16.37%, #3645BE 24.83%, #2A36B4 33.74%, #202CAE 41.88%, #1923A7 47.59%, #1B1E9B 55%, #202199 61.87%, #171891 69.31%, #12158A 79.48%, #0F1476 89.22%, #0C156E 100%);
`

function LuxPlusMonthFreePreviewInfoModal() {
  const dispatch = useAppDispatch()
  const modalElement = useModalElementContext()
  const dismiss = useCallback<ModalFooterOnClickHandler>(() => {
    modalElement.resolve()
  }, [modalElement])
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(() => subscriptionProduct.offers?.find(offer => !offer.joinFeeWaived), [subscriptionProduct.offers])

  useEffect(() => {
    dispatch({ type: ACKNOWLEDGE_LUX_PLUS_MONTH_FREE_PREVIEW_MODAL })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <ModalBase>
    <ModalBody>
      <Hero padding={20} paletteType="inverse">
        <Group direction="vertical" horizontalAlign="center" gap={12} tabletGap={16}>
          <Pill kind="primary" variant="success">{LUXURY_PLUS.FREE_PREVIEW_DURATION}-day Free Preview</Pill>
          <Group direction="vertical" horizontalAlign="center" gap={4} tabletGap={8}>
            <Heading variant="heading2">
              {!!firstName && <>{firstName}, browse like a VIP during</>}
              {!firstName && <>Browse like a VIP during</>}
            </Heading>
            <Heading variant="heading2">
              <LuxPlusLogo height="0.65em" /> Month
            </Heading>
          </Group>
        </Group>
      </Hero>
      <ModalContent>
        <Group direction="vertical" gap={16} tabletGap={24}>
          <Group direction="vertical" gap={12}>
            <BodyText variant="large">
              Your <b>{LUXURY_PLUS.FREE_PREVIEW_DURATION}-day free preview</b> is now active. Enjoy VIP perks, hidden deals and early access while you explore.
            </BodyText>
            {!!offer && <BodyText variant="large">
              Unlock unlimited year-round benefits for <FormatCurrency value={offer.price} /> a year, with a one-time <FormatCurrency value={offer.joinFee} /> joining fee — including:
            </BodyText>}
          </Group>
          <Group direction="vertical" gap={8}>
            <BodyTextBlock variant="medium" startIcon={<LineBedIcon colour="lux-plus" />}>
              Save hundreds on hotel offers, tours and local experiences every year.
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineStarIcon colour="lux-plus" />}>
              Bonus inclusions: hotel credit, daily breakfast, upgrades & more on select hotels
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LineUnlockAltIcon colour="lux-plus" />}>
              Early access to our bestselling offers plus hidden offers, exclusive to members
            </BodyTextBlock>
            <BodyTextBlock variant="medium" startIcon={<LinePlusCircleIcon colour="lux-plus" />}>
              <Colour value="lux-plus">PLUS</Colour> so much more
            </BodyTextBlock>
          </Group>
          {!!offer && <MessageBanner kind="promotional-primary" icon={<SolidTagAltIcon />}>
            <b>Special offer: </b> No joining fee when you purchase an eligible {LUXURY_PLUS.PROGRAM_NAME} offer. <b>Valued at <FormatCurrency value={offer.joinFee} /></b>
          </MessageBanner>}
        </Group>
      </ModalContent>
      <ModalFooter
        primaryActionProps={{
          children: <>Continue to {LUXURY_PLUS.PROGRAM_NAME} Month</>,
          onClick: dismiss,
        }}
      />
    </ModalBody>
  </ModalBase>
}

export default LuxPlusMonthFreePreviewInfoModal
